import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import { entrepriseService } from '../../lib/api';
import { toast } from 'react-toastify';

const ImportEmployeesModal = ({ isOpen, onClose, onImportSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [updateDuplicates, setUpdateDuplicates] = useState(true);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) {
      toast.error('Aucun fichier sélectionné');
      return;
    }

    const file = acceptedFiles[0];
    
    // Vérifier le type de fichier
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      toast.error('Format de fichier non supporté. Utilisez CSV ou Excel (.xlsx)');
      return;
    }

    // Vérifier la taille (max 5 MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux. Taille maximum: 5 MB');
      return;
    }

    setUploading(true);
    setResults(null);
    setShowResults(false);

    try {
      const response = await entrepriseService.importEmployees(file, updateDuplicates);
      setResults(response);
      setShowResults(true);
      
      if (response.erreurs === 0) {
        toast.success(`✅ Import réussi ! ${response.crees} employés créés, ${response.mis_a_jour} mis à jour`);
        if (onImportSuccess) {
          onImportSuccess();
        }
      } else {
        toast.warning(`⚠️ Import terminé avec ${response.erreurs} erreur(s)`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'import:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import des employés');
    } finally {
      setUploading(false);
    }
  }, [updateDuplicates, onImportSuccess]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    multiple: false,
    disabled: uploading
  });

  const handleDownloadTemplate = async () => {
    try {
      const blob = await entrepriseService.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_import_employes.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('📥 Modèle CSV téléchargé');
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      toast.error('Erreur lors du téléchargement du modèle');
    }
  };

  const handleClose = () => {
    setResults(null);
    setShowResults(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h3 className="text-lg font-medium">📥 Importer des Employés</h3>
          <Button size="sm" variant="outline" onClick={handleClose}>
            ✖️ Fermer
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {!showResults ? (
            <>
              {/* Instructions */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <h4 className="font-medium text-blue-900 mb-2">📋 Instructions</h4>
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Téléchargez le modèle CSV en cliquant sur le bouton ci-dessous</li>
                    <li>Remplissez le fichier avec les informations de vos employés</li>
                    <li><strong>Colonnes obligatoires :</strong> matricule, nom, prénom, téléphone, salaire_net, date_naissance, date_embauche, contrat_type, wallet</li>
                    <li><strong>Email :</strong> optionnel</li>
                    <li><strong>Format téléphone :</strong> XXXXXXXXX (9 chiffres, sans indicatif - ex: 621234567)</li>
                    <li><strong>Format dates :</strong> JJ/MM/AAAA (ex: 15/05/1990)</li>
                    <li><strong>Types de contrat :</strong> CDI, CDD, STAGE, INTERIM</li>
                    <li><strong>Format wallet :</strong> 9 chiffres (ex: 621234567)</li>
                    <li>Importez le fichier (CSV ou Excel) en le glissant ci-dessous ou en cliquant</li>
                  </ol>
                </CardContent>
              </Card>

              {/* Télécharger le modèle */}
              <div>
                <Button onClick={handleDownloadTemplate} className="w-full sm:w-auto">
                  📥 Télécharger le Modèle CSV
                </Button>
              </div>

              {/* Option doublons */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="updateDuplicates"
                  checked={updateDuplicates}
                  onChange={(e) => setUpdateDuplicates(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="updateDuplicates" className="text-sm text-gray-700">
                  Mettre à jour les employés existants (doublons)
                </label>
              </div>

              {/* Zone de drop */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-blue-500 bg-blue-50'
                    : uploading
                    ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                }`}
              >
                <input {...getInputProps()} />
                {uploading ? (
                  <div className="space-y-3">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600">Traitement en cours...</p>
                    <p className="text-sm text-gray-500">Veuillez patienter</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="text-5xl">📁</div>
                    {isDragActive ? (
                      <p className="text-blue-600 font-medium">Déposez le fichier ici...</p>
                    ) : (
                      <>
                        <p className="text-gray-700 font-medium">
                          Glissez-déposez un fichier ici, ou cliquez pour sélectionner
                        </p>
                        <p className="text-sm text-gray-500">
                          Formats acceptés : CSV, Excel (.xlsx, .xls) • Taille max : 5 MB
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Avertissement */}
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="p-4">
                  <div className="flex items-start">
                    <div className="text-yellow-500 text-xl mr-3">⚠️</div>
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Important :</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Un PIN à 4 chiffres sera généré automatiquement pour chaque employé</li>
                        <li>Les PINs seront affichés une seule fois après l'import</li>
                        <li>Transmettez les PINs aux employés de manière sécurisée</li>
                        <li>Maximum 500 employés par import</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Résultats de l'import */}
              <div className="space-y-6">
                {/* Statistiques */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {results.total_lignes}
                      </div>
                      <div className="text-sm text-blue-800">Total lignes</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {results.crees}
                      </div>
                      <div className="text-sm text-green-800">Créés</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-orange-600">
                        {results.mis_a_jour}
                      </div>
                      <div className="text-sm text-orange-800">Mis à jour</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-4 text-center">
                      <div className="text-3xl font-bold text-red-600">
                        {results.erreurs}
                      </div>
                      <div className="text-sm text-red-800">Erreurs</div>
                    </CardContent>
                  </Card>
                </div>

                {/* PINs générés */}
                {Object.keys(results.pins).length > 0 && (
                  <Card className="bg-yellow-50 border-yellow-200">
                    <CardContent className="p-4">
                      <h4 className="font-medium text-yellow-900 mb-3 flex items-center">
                        <span className="text-xl mr-2">🔑</span>
                        Codes PIN Générés (à transmettre aux employés)
                      </h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {Object.entries(results.pins).map(([telephone, data]) => (
                          <div
                            key={telephone}
                            className="bg-white border border-yellow-200 rounded p-3 flex items-center justify-between"
                          >
                            <div>
                              <p className="font-medium text-gray-900">{data.nom}</p>
                              <p className="text-sm text-gray-600">{telephone}</p>
                              {data.email && (
                                <p className="text-xs text-gray-500">{data.email}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-yellow-700 font-mono">
                                {data.pin}
                              </div>
                              <div className="text-xs text-gray-500">
                                {data.action === 'created' ? 'Nouveau' : 'Mis à jour'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-yellow-800 mt-3">
                        ⚠️ Ces PINs sont affichés une seule fois. Copiez-les avant de fermer cette fenêtre.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Employés créés */}
                {results.details.success.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-medium text-green-900 mb-3">
                        ✅ Employés Créés ({results.details.success.length})
                      </h4>
                      <div className="max-h-48 overflow-y-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left py-2 px-3">Ligne</th>
                              <th className="text-left py-2 px-3">Matricule</th>
                              <th className="text-left py-2 px-3">Nom</th>
                              <th className="text-left py-2 px-3">Téléphone</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.details.success.map((emp, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="py-2 px-3">{emp.ligne}</td>
                                <td className="py-2 px-3">{emp.matricule}</td>
                                <td className="py-2 px-3">{emp.nom}</td>
                                <td className="py-2 px-3">{emp.telephone}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Employés mis à jour */}
                {results.details.updated.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-medium text-orange-900 mb-3">
                        🔄 Employés Mis à Jour ({results.details.updated.length})
                      </h4>
                      <div className="max-h-48 overflow-y-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left py-2 px-3">Ligne</th>
                              <th className="text-left py-2 px-3">Matricule</th>
                              <th className="text-left py-2 px-3">Nom</th>
                              <th className="text-left py-2 px-3">Téléphone</th>
                            </tr>
                          </thead>
                          <tbody>
                            {results.details.updated.map((emp, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="py-2 px-3">{emp.ligne}</td>
                                <td className="py-2 px-3">{emp.matricule}</td>
                                <td className="py-2 px-3">{emp.nom}</td>
                                <td className="py-2 px-3">{emp.telephone}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Erreurs */}
                {results.details.errors.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-medium text-red-900 mb-3">
                        ❌ Erreurs ({results.details.errors.length})
                      </h4>
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {results.details.errors.map((err, idx) => (
                          <div
                            key={idx}
                            className="bg-red-50 border border-red-200 rounded p-3"
                          >
                            <div className="flex items-start justify-between mb-1">
                              <div className="font-medium text-red-900">
                                Ligne {err.ligne} - {err.nom}
                              </div>
                              <div className="text-sm text-red-700">{err.matricule}</div>
                            </div>
                            <ul className="text-sm text-red-700 list-disc list-inside">
                              {err.erreurs.map((e, i) => (
                                <li key={i}>{e}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Boutons d'action */}
                <div className="flex space-x-3">
                  <Button onClick={handleClose} className="flex-1">
                    ✅ Terminer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setResults(null);
                      setShowResults(false);
                    }}
                    className="flex-1"
                  >
                    📥 Importer un Autre Fichier
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportEmployeesModal;
